import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSearch, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import serverURL from '../config';

const AssigneeFeedbackList = ({ eventId, levelId }) => {
  const STORAGE_KEY_ASSIGNEES = `feedback_assignees_${eventId}_${levelId}`;
  const STORAGE_KEY_FEEDBACK = `feedback_responses_${eventId}_${levelId}`;

  const [candidates, setCandidates] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateFeedback, setCandidateFeedback] = useState({});
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [newAssignee, setNewAssignee] = useState({
    interviewer_name: '',
    interviewer_email: '',
    interview_type: 'Online'
  });

  const [feedbackForm, setFeedbackForm] = useState({
    feedback_title: '',
    feedback_text: '',
    rating: 3
  });

  // Load data from localStorage
  useEffect(() => {
    loadData();
    fetchCandidates();
  }, [eventId, levelId]);

  const loadData = () => {
    try {
      const storedAssignees = localStorage.getItem(STORAGE_KEY_ASSIGNEES);
      const storedFeedback = localStorage.getItem(STORAGE_KEY_FEEDBACK);
      
      if (storedAssignees) {
        setAssignees(JSON.parse(storedAssignees));
      }
      if (storedFeedback) {
        setCandidateFeedback(JSON.parse(storedFeedback));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    const token = localStorage.getItem('token');
    try {
      // Fetch candidates registered for this event from candidate_level_progress
      const response = await axios.get(`${serverURL}/events/${eventId}/candidates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates(response.data || []);
    } catch (error) {
      // If endpoint doesn't exist, create mock candidates
      console.error('Failed to fetch candidates:', error);
      setCandidates([]);
    }
  };

  const saveAssignees = (updated) => {
    try {
      localStorage.setItem(STORAGE_KEY_ASSIGNEES, JSON.stringify(updated));
      setAssignees(updated);
    } catch (error) {
      console.error('Failed to save assignees:', error);
      alert('Failed to save assignee');
    }
  };

  const saveFeedback = (updated) => {
    try {
      localStorage.setItem(STORAGE_KEY_FEEDBACK, JSON.stringify(updated));
      setCandidateFeedback(updated);
    } catch (error) {
      console.error('Failed to save feedback:', error);
      alert('Failed to save feedback');
    }
  };

  const handleAddAssignee = () => {
    if (!newAssignee.interviewer_name || !newAssignee.interviewer_email) {
      alert('Please fill in all fields');
      return;
    }

    const assignee = {
      id: Date.now(),
      ...newAssignee
    };

    saveAssignees([...assignees, assignee]);
    setNewAssignee({ interviewer_name: '', interviewer_email: '', interview_type: 'Online' });
    setIsAssigneeModalOpen(false);
  };

  const handleRemoveAssignee = (id) => {
    if (window.confirm('Are you sure you want to remove this interviewer?')) {
      saveAssignees(assignees.filter(a => a.id !== id));
    }
  };

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    const key = `candidate_${candidate.id}`;
    if (candidateFeedback[key]) {
      setFeedbackForm(candidateFeedback[key]);
    } else {
      setFeedbackForm({
        feedback_title: '',
        feedback_text: '',
        rating: 3
      });
    }
  };

  const handleSubmitFeedback = () => {
    if (!selectedCandidate) {
      alert('Please select a candidate');
      return;
    }

    if (!feedbackForm.feedback_title || !feedbackForm.feedback_text) {
      alert('Please fill in feedback title and text');
      return;
    }

    const key = `candidate_${selectedCandidate.id}`;
    const updated = {
      ...candidateFeedback,
      [key]: {
        ...feedbackForm,
        submitted_at: new Date().toISOString(),
        candidate_id: selectedCandidate.id
      }
    };

    saveFeedback(updated);
    setIsFeedbackModalOpen(false);
    alert('Feedback submitted successfully');
  };

  const filteredCandidates = candidates.filter(candidate =>
    candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    candidate.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCandidateFeedback = (candidateId) => {
    const key = `candidate_${candidateId}`;
    return candidateFeedback[key];
  };

  const renderStars = (rating) => {
    const filledStars = '★'.repeat(rating);
    const emptyStars = '☆'.repeat(5 - rating);
    return <span className="text-yellow-500 text-lg">{filledStars}{emptyStars}</span>;
  };

  if (loading) {
    return <div className="p-4 text-center">Loading feedback management...</div>;
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Interviewers/Assignees List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-semibold">Interviewers ({assignees.length})</h2>
              <button
                onClick={() => setIsAssigneeModalOpen(true)}
                className="text-blue-500 hover:text-blue-700"
                title="Add interviewer"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {assignees.length === 0 ? (
                <div className="p-4 text-gray-500 text-center text-sm">No interviewers assigned yet</div>
              ) : (
                assignees.map(assignee => (
                  <div key={assignee.id} className="p-3 border-b hover:bg-gray-50 transition flex justify-between items-start">
                    <div className="flex-grow">
                      <p className="font-semibold text-sm">{assignee.interviewer_name}</p>
                      <p className="text-xs text-gray-600">{assignee.interviewer_email}</p>
                      <span className="inline-block mt-1 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                        {assignee.interview_type}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveAssignee(assignee.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove interviewer"
                    >
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Candidates and Feedback */}
        <div className="lg:col-span-2">
          <div className="space-y-4">
            {/* Candidates Search */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-4 border-b">
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {filteredCandidates.length === 0 ? (
                  <div className="p-4 text-gray-500 text-center">
                    {candidates.length === 0 ? 'No candidates registered for this event' : 'No matching candidates'}
                  </div>
                ) : (
                  filteredCandidates.map(candidate => {
                    const feedback = getCandidateFeedback(candidate.id);
                    return (
                      <div
                        key={candidate.id}
                        className={`p-4 border-b cursor-pointer hover:bg-blue-50 transition ${
                          selectedCandidate?.id === candidate.id ? 'bg-blue-100' : ''
                        }`}
                        onClick={() => handleSelectCandidate(candidate)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-grow">
                            <p className="font-semibold text-sm">{candidate.name}</p>
                            <p className="text-xs text-gray-600">{candidate.email}</p>
                            {feedback && (
                              <div className="mt-2">
                                <div className="flex items-center gap-2 mb-1">
                                  {renderStars(feedback.rating)}
                                  <span className="text-xs text-gray-600">({feedback.rating}/5)</span>
                                </div>
                                <p className="text-xs text-gray-700 truncate">{feedback.feedback_title}</p>
                              </div>
                            )}
                          </div>
                          {feedback && (
                            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 ml-2">
                              Submitted
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Feedback Details */}
            {selectedCandidate && (
              <div className="bg-white rounded-lg shadow-md">
                <div className="p-4 border-b flex justify-between items-center">
                  <h3 className="font-semibold">Feedback for {selectedCandidate.name}</h3>
                  <button
                    onClick={() => setIsFeedbackModalOpen(true)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    {getCandidateFeedback(selectedCandidate.id) ? 'Edit Feedback' : 'Add Feedback'}
                  </button>
                </div>
                
                {getCandidateFeedback(selectedCandidate.id) ? (
                  <div className="p-4">
                    <h4 className="font-semibold mb-2">{getCandidateFeedback(selectedCandidate.id).feedback_title}</h4>
                    <div className="mb-3">
                      {renderStars(getCandidateFeedback(selectedCandidate.id).rating)}
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {getCandidateFeedback(selectedCandidate.id).feedback_text}
                    </p>
                    <p className="text-xs text-gray-500 mt-3">
                      Submitted: {new Date(getCandidateFeedback(selectedCandidate.id).submitted_at).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 text-gray-500 text-center">No feedback submitted yet</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Assignee Modal */}
      {isAssigneeModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Interviewer</h2>
              <button
                onClick={() => setIsAssigneeModalOpen(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold">Interviewer Name *</label>
                <input
                  type="text"
                  value={newAssignee.interviewer_name}
                  onChange={(e) => setNewAssignee({ ...newAssignee, interviewer_name: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter name"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Email *</label>
                <input
                  type="email"
                  value={newAssignee.interviewer_email}
                  onChange={(e) => setNewAssignee({ ...newAssignee, interviewer_email: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter email"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Interview Type</label>
                <select
                  value={newAssignee.interview_type}
                  onChange={(e) => setNewAssignee({ ...newAssignee, interview_type: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                  <option value="Phone">Phone</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsAssigneeModalOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAssignee}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add Interviewer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {isFeedbackModalOpen && selectedCandidate && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Feedback for {selectedCandidate.name}</h2>
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-semibold">Feedback Title *</label>
                <input
                  type="text"
                  value={feedbackForm.feedback_title}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_title: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter feedback title"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Feedback *</label>
                <textarea
                  value={feedbackForm.feedback_text}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_text: e.target.value })}
                  className="w-full p-2 border rounded-md h-24"
                  placeholder="Enter detailed feedback"
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                      className={`text-2xl ${
                        star <= feedbackForm.rating ? 'text-yellow-500' : 'text-gray-300'
                      }`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssigneeFeedbackList;
